import { TranslationsDTO } from '@library/dto/shared/translations.dto';
import { QpLocaleService } from '@library/services/qp-locale/qp-locale.service';
import { SERVER_API_URL } from '@one/app/app.constants';
import { IDefectsChecklistDefect } from '@one/app/pages/isp/pages/inspection/pages/id/pages/defects-checklist/isp-inspection-id-defect.models';
import { DatabaseService } from '@one/app/shared/services/database/database-inspection.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';

const CACHE_EXPIRATION_MS = 6 * 60 * 60 * 1000; // 6 hours
/** In-memory cache for inspection-scoped library (defects with translations). */
const TRANSLATED_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
/** Max number of inspection entries to keep in translated cache; evict oldest when exceeded. */
const TRANSLATED_CACHE_MAX_ENTRIES = 20;

interface IQimaPublicDefectsResponse {
  defects: IDefectsChecklistDefect[];
}

/**
 * Normalize string for search: lowercase, remove accents, preserve spaces
 */
function normalizeForSearch(text: string): string {
  if (!text) {
    return '';
  }

  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Resolve display value for a field (translated when available for current locale).
 * Uses standard TranslationsDTO shape; locale is a string (from QpLocaleService / API).
 */
function fieldForLocale(defect: IDefectsChecklistDefect, field: 'name' | 'description', locale: string): string {
  const translations: TranslationsDTO<IDefectsChecklistDefect> | undefined = defect.translations;
  const byLocale = translations?.[locale as keyof TranslationsDTO<IDefectsChecklistDefect>];
  const value = byLocale?.[field];

  return value ?? (field === 'name' ? defect.name : (defect.description ?? '')) ?? '';
}

@Injectable({
  providedIn: 'root',
})
export class QimaPublicDefectsService {
  private readonly _resourceUrl = `${SERVER_API_URL}api/public-defects`;

  /** In-memory cache for inspection-scoped list (defects with translations). */
  private readonly _translatedCache = new Map<number, { defects: IDefectsChecklistDefect[]; lastUpdated: number }>();

  public constructor(
    private readonly _databaseService: DatabaseService,
    private readonly _httpClient: HttpClient,
    private readonly _qpLocaleService: QpLocaleService
  ) {}

  /**
   * Refresh cache if needed (if no data or >6 hours old).
   * When {@code inspectionId} is provided, fetches with that param so the API returns defects
   * with {@code translations} populated (for use with the translatedField pipe).
   *
   * @param inspectionId optional; when present, response includes translations from snapshot
   * @returns Observable of defects list (cached or fresh)
   */
  public refreshCacheIfNeeded$(inspectionId?: number): Observable<IDefectsChecklistDefect[]> {
    if (inspectionId != null) {
      return this._refreshTranslatedCacheIfNeeded$(inspectionId);
    }

    return from(this._databaseService.getQimaPublicDefectsCache()).pipe(
      switchMap((cache): Observable<IDefectsChecklistDefect[]> => {
        const now = Date.now();
        const isCacheValid = cache?.lastUpdated && now - cache.lastUpdated < CACHE_EXPIRATION_MS;

        if (isCacheValid && cache) {
          return of(cache.defects);
        }

        return this._fetchFromApiForPersistence$().pipe(
          switchMap((defects): Observable<IDefectsChecklistDefect[]> => this._persistDefectsToDexie$(defects)),
          catchError((error): Observable<IDefectsChecklistDefect[]> => {
            console.error('Failed to fetch QIMA public defects from API:', error);

            return of(cache?.defects ?? []);
          })
        );
      }),
      catchError((error): Observable<IDefectsChecklistDefect[]> => {
        console.error('Failed to read QIMA public defects cache:', error);

        return this._fetchFromApiForPersistence$().pipe(
          switchMap((defects): Observable<IDefectsChecklistDefect[]> => this._persistDefectsToDexie$(defects)),
          catchError((fetchError): Observable<IDefectsChecklistDefect[]> => {
            console.error('Failed to fetch QIMA public defects from API:', fetchError);

            return of([]);
          })
        );
      })
    );
  }

  /**
   * Search QIMA public defects database
   * @param searchTerm Search term to filter defects
   * @param excludeDefects List of defects to exclude (already added defects)
   * @param maxResults Maximum number of results to return (default: 10)
   * @returns Observable of matching defects (limited by maxResults)
   */
  public searchQimaPublicDefects$(
    searchTerm: string,
    excludeDefects: IDefectsChecklistDefect[] = [],
    maxResults: number = 10
  ): Observable<IDefectsChecklistDefect[]> {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return of([]);
    }

    return this.refreshCacheIfNeeded$().pipe(
      map((allDefects): IDefectsChecklistDefect[] => {
        const cleanedSearch = normalizeForSearch(searchTerm);
        const excludedDefectsSet = new Set<string>();

        excludeDefects.forEach((defect): void => {
          if (defect.name && defect.classification) {
            const key = `${normalizeForSearch(defect.name)}|${defect.classification}`;

            excludedDefectsSet.add(key);
          }
        });

        const matchingDefects = allDefects.filter((defect): boolean => {
          const nameMatch = normalizeForSearch(defect.name).includes(cleanedSearch);
          const descMatch = defect.description ? normalizeForSearch(defect.description).includes(cleanedSearch) : false;

          if (!nameMatch && !descMatch) {
            return false;
          }

          if (defect.name && defect.classification) {
            const key = `${normalizeForSearch(defect.name)}|${defect.classification}`;

            if (excludedDefectsSet.has(key)) {
              return false;
            }
          }

          return true;
        });

        return matchingDefects.slice(0, maxResults);
      })
    );
  }

  /**
   * Searches the public defect library using the same list as {@link searchQimaPublicDefects$}
   * but loaded with {@code inspectionId} so each defect has {@code translations} populated.
   * Search matches against translated name/description when available for the current locale.
   * Use the {@code translatedField} pipe in templates to display translated values.
   */
  public searchQimaPublicDefectsWithTranslations$(
    searchTerm: string,
    inspectionId: number,
    excludeDefects: IDefectsChecklistDefect[] = [],
    maxResults: number = 10
  ): Observable<IDefectsChecklistDefect[]> {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return of([]);
    }

    const locale = this._qpLocaleService.getCurrentLocale();

    return this.refreshCacheIfNeeded$(inspectionId).pipe(
      map((allDefects): IDefectsChecklistDefect[] => {
        const cleanedSearch = normalizeForSearch(searchTerm);
        const excludedDefectsSet = new Set<string>();

        excludeDefects.forEach((defect): void => {
          if (defect.name && defect.classification) {
            excludedDefectsSet.add(`${normalizeForSearch(defect.name)}|${defect.classification}`);
          }
        });

        return allDefects
          .filter((defect): boolean => {
            const translatedName = fieldForLocale(defect, 'name', locale);
            const translatedDesc = fieldForLocale(defect, 'description', locale);
            const nameMatch =
              normalizeForSearch(defect.name).includes(cleanedSearch) || normalizeForSearch(translatedName).includes(cleanedSearch);
            const descMatch =
              normalizeForSearch(defect.description ?? '').includes(cleanedSearch) ||
              normalizeForSearch(translatedDesc).includes(cleanedSearch);

            if (!nameMatch && !descMatch) {
              return false;
            }

            if (defect.name && defect.classification) {
              const key = `${normalizeForSearch(defect.name)}|${defect.classification}`;

              if (excludedDefectsSet.has(key)) {
                return false;
              }
            }

            return true;
          })
          .slice(0, maxResults);
      })
    );
  }

  private _refreshTranslatedCacheIfNeeded$(inspectionId: number): Observable<IDefectsChecklistDefect[]> {
    const now = Date.now();

    this._evictStaleTranslatedCacheEntries(now);

    const entry = this._translatedCache.get(inspectionId);

    if (entry && now - entry.lastUpdated < TRANSLATED_CACHE_TTL_MS) {
      return of(entry.defects);
    }

    return this._fetchFromApiForPersistence$(inspectionId).pipe(
      switchMap((defects): Observable<IDefectsChecklistDefect[]> => {
        if (this._translatedCache.size >= TRANSLATED_CACHE_MAX_ENTRIES) {
          const oldestKey = this._translatedCache.keys().next().value as number | undefined;

          if (oldestKey !== undefined) {
            this._translatedCache.delete(oldestKey);
          }
        }

        this._translatedCache.set(inspectionId, { defects, lastUpdated: now });

        return this._persistDefectsToDexie$(defects);
      }),
      catchError((error): Observable<IDefectsChecklistDefect[]> => {
        console.error('Failed to fetch QIMA public defects from API:', error);

        return of(entry?.defects ?? []);
      })
    );
  }

  /** Remove entries that have exceeded TTL to free memory. */
  private _evictStaleTranslatedCacheEntries(now: number): void {
    for (const [id, entry] of this._translatedCache.entries()) {
      if (now - entry.lastUpdated >= TRANSLATED_CACHE_TTL_MS) {
        this._translatedCache.delete(id);
      }
    }
  }

  private _persistDefectsToDexie$(defects: IDefectsChecklistDefect[]): Observable<IDefectsChecklistDefect[]> {
    return from(this._databaseService.saveQimaPublicDefectsCache(defects)).pipe(
      map((): IDefectsChecklistDefect[] => defects),
      catchError((error): Observable<IDefectsChecklistDefect[]> => {
        console.error('Failed to save QIMA public defects cache:', error);

        return of(defects);
      })
    );
  }

  private _fetchFromApiForPersistence$(inspectionId?: number): Observable<IDefectsChecklistDefect[]> {
    const url = inspectionId != null ? `${this._resourceUrl}?inspectionId=${inspectionId}` : this._resourceUrl;

    return this._httpClient
      .get<IQimaPublicDefectsResponse>(url, { observe: 'body' })
      .pipe(map((response): IDefectsChecklistDefect[] => response.defects));
  }
}
